/**
 * Every public Omens page served by this app and what crawlers get for it (GEO PRD, Lote 0): `<title>`, meta
 * description, canonical, sitemap and `lastmod`. _app renders it in the server HTML of every page, also for the
 * pages still behind the client-only PersistGate, and CharlesMeta uses the same values once the page renders.
 * No imports: the middleware and the API handler specs load it.
 */

export interface OmensPageSeo {
  title: string
  description: string
  /** Date the page's content last changed (`git log -1 --format=%cs -- <page files>`), never the build date. */
  lastmod: string
  /** Real duplicates only (PRD 3.1): the page they copy. Out of the sitemap. Default: the page itself. */
  canonical?: string
  /** Out of the sitemap and the index (`noindex, follow`, also as `X-Robots-Tag` in next.config.js). */
  noindex?: boolean
  /** Shared with the other brands: Omens tags only when the server saw an Omens host (`pageProps.omensHost`). */
  hostScoped?: boolean
}

export const OMENS_PAGES: Readonly<Record<string, OmensPageSeo>> = {
  // The GEO home, promoted from /home-v2 on 28/10/2026 (Morgan): 301 in next.config.js. Same description as
  // HOME_META_DESCRIPTION (modules/landings/geo/content/home), the one Morgan validated.
  '/': {
    title: 'Omens: saúde masculina online com urologistas e dermatologistas',
    description:
      'A Omens é uma plataforma brasileira de saúde masculina online: urologistas, dermatologistas e psicólogos por vídeo, telefone ou mensagem, desde R$ 79.',
    lastmod: '2026-10-28',
    hostScoped: true
  },
  // Courses: the GEO template pages, promoted from their -v2 copies on 28/10/2026 (Morgan): 301 in next.config.js.
  // Same title and description as the page's content (ProgramLanding/content, checked by its spec).
  '/cursos/programa-control': {
    title: 'Programa Control: curso para controlar a ejaculação precoce | Omens',
    description:
      'Curso online de 90 dias criado pelo urologista Dr. João Brunhara: 13 técnicas físicas e mentais, 15 minutos por dia, com acompanhamento.',
    lastmod: '2026-10-28'
  },
  '/cursos/programa-master': {
    title: 'Programa Master: curso com exercícios para disfunção erétil | Omens',
    description:
      'Curso online de 8 semanas criado pelo urologista Dr. João Brunhara: 12 técnicas físicas e mentais para a ereção, 15 minutos por dia, com acompanhamento.',
    lastmod: '2026-10-28'
  },
  '/cursos/programa-control/9-taticas-de-tratamento-para-superar-a-ejaculacao-precoce': {
    title: '9 táticas de tratamento para superar a ejaculação precoce | Omens',
    description:
      'Da técnica start-stop ao treino do assoalho pélvico: 9 táticas para tratar a ejaculação precoce e quando procurar um urologista.',
    lastmod: '2026-10-24'
  },
  '/cursos/programa-control/tipos-de-tratamento-e-prevencao-da-ejaculacao-precoce': {
    title: 'Tipos de tratamento e prevenção da ejaculação precoce | Omens',
    description:
      'Exercícios, terapia e tratamento medicamentoso: conheça os tipos de tratamento da ejaculação precoce e o que ajuda a prevenir o problema.',
    lastmod: '2026-10-24'
  },
  '/cursos/programa-control/veja-dicas-e-um-passo-a-passo-de-como-nao-ejacular-rapido': {
    title: 'Como não ejacular rápido: dicas e passo a passo | Omens',
    description:
      'Quer ter mais tempo na hora do sexo? Veja dicas e um passo a passo para retardar a ejaculação e prolongar o prazer.',
    lastmod: '2026-10-24'
  },
  '/cursos/programa-control/veja-o-metodo-que-acabou-com-a-expressao-gozando-rapido': {
    title: 'O método que acabou com o "gozando rápido" | Omens',
    description:
      'Gozar nos primeiros minutos da relação tem tratamento. Veja o método de exercícios criado por urologistas para controlar a ejaculação.',
    lastmod: '2026-10-24'
  },
  // Conditions (PRD 3.1, 3.2 and 3.5). The GEO pages promoted from their -v2 copies on 28/10/2026 (Morgan) carry
  // that lastmod: 301 in next.config.js (PROMOTED_GEO_PAGES).
  // /problemas-de-erecao merged into it (Morgan's PRD of 25/10/2026): 301 in next.config.js
  '/disfuncao-eretil': {
    title: 'Disfunção erétil: consulta online com urologista | Omens',
    description:
      'Disfunção erétil (problemas de ereção) tem tratamento. Consulta online com urologista a partir de R$ 79, com discrição e acompanhamento. Mais de 35 mil pacientes.',
    lastmod: '2026-10-25'
  },
  '/ejaculacao-precoce': {
    title: 'Ejaculação precoce: tratamento online com urologista | Omens',
    description:
      'Ejaculação precoce tem tratamento. Consulte um urologista online a partir de R$ 89 e receba um tratamento personalizado em casa, com discrição.',
    lastmod: '2026-10-28'
  },
  '/impotencia-sexual': {
    title: 'Impotência sexual: tratamento online com urologista | Omens',
    description:
      'Impotência sexual tem tratamento. Consulte um urologista online e receba em casa o tratamento indicado na consulta, com discrição.',
    lastmod: '2023-08-09',
    canonical: '/disfuncao-eretil'
  },
  '/quando-o-homem-ejacula-rapido': {
    title: 'Ejacular rápido: por que acontece e como tratar | Omens',
    description:
      'Goza rápido demais? Entenda por que acontece e converse com um urologista online. Tratamento personalizado entregue em casa, com discrição.',
    lastmod: '2026-10-28'
  },
  '/perda-de-libido': {
    title: 'Perda de libido masculina: causas e tratamento online | Omens',
    description:
      'Falta de desejo sexual tem causa e tratamento. Consulte um urologista online, investigue a causa e receba um tratamento personalizado em casa.',
    lastmod: '2026-10-28'
  },
  '/saude-sexual': {
    title: 'Saúde sexual masculina: consulta online com urologista | Omens',
    description:
      'Ejaculação precoce, disfunção erétil, perda de libido e próstata: consulte um urologista online e receba um tratamento personalizado em casa.',
    lastmod: '2026-10-28'
  },
  '/problemas-sexuais': {
    title: 'Problemas sexuais masculinos: consulta online | Omens',
    description:
      'Ejaculação rápida, ereção fraca ou falta de desejo? Converse com um especialista online, com discrição, e receba um tratamento personalizado em casa.',
    lastmod: '2026-10-28'
  },
  '/harmonizacao-peniana': {
    title: 'Harmonização peniana: como funciona, preço e cuidados | Omens',
    description:
      'Entenda a harmonização peniana: como é o procedimento, quem pode fazer, quanto custa e quais os cuidados. Avaliação com médico especialista.',
    lastmod: '2026-10-28'
  },
  '/hpb': {
    title: 'HPB (hiperplasia prostática benigna): tratamento online | Omens',
    description:
      'Hiperplasia prostática benigna tem tratamento. Consulte um urologista online e receba em casa o tratamento indicado na consulta.',
    lastmod: '2024-06-07',
    canonical: '/prostata-aumentada'
  },
  '/prostata-aumentada': {
    title: 'Próstata aumentada (HPB): sintomas e tratamento online | Omens',
    description:
      'Jato urinário fraco e vontade frequente de urinar podem ser próstata aumentada. Consulte um urologista online e receba um tratamento personalizado em casa.',
    lastmod: '2026-10-28'
  },
  '/prep': {
    title: 'PrEP: prevenção do HIV com acompanhamento online | Omens',
    description:
      'Saiba se a PrEP é indicada para você. Consulte um especialista online e faça a PrEP pelo SUS ou no particular, com acompanhamento e discrição.',
    lastmod: '2026-10-28'
  },
  '/psicologia-sexual-parceria': {
    title: 'Psicólogo sexual online: terapia individual e de casal | Omens',
    description:
      'Terapia sexual online com psicólogos: ansiedade de desempenho, desejo, relacionamento e vida sexual do casal. Sessões por vídeo, com sigilo.',
    lastmod: '2026-10-28'
  },
  // Skin and hair GEO pages promoted from their -v2 copies on 26/10/2026 (Morgan): 301 in next.config.js
  '/calvicie': {
    title: 'Calvície: tratamento online para queda de cabelo | Omens',
    description:
      'Calvície tem tratamento. Consulte um dermatologista online e receba em casa tratamentos tópicos e orais para a queda de cabelo, com discrição.',
    lastmod: '2026-10-26'
  },
  '/acne': {
    title: 'Acne: tratamento online com dermatologista | Omens',
    description:
      'Acne tem tratamento. Consulte um dermatologista online, receba um tratamento personalizado para a sua pele e acompanhe a evolução sem sair de casa.',
    lastmod: '2026-10-26'
  },
  '/dermatite': {
    title: 'Caspa e dermatite seborreica: tratamento online | Omens',
    description:
      'Caspa que não passa pode ser dermatite seborreica. Consulte um dermatologista online e receba um tratamento personalizado em casa.',
    lastmod: '2026-10-26'
  },
  '/dermatologia': {
    title: 'Dermatologia online: pele, cabelo e acne | Omens',
    description:
      'Consulte um dermatologista online para queda de cabelo, acne, caspa e cuidados com a pele. Tratamento personalizado entregue em casa.',
    lastmod: '2026-10-26'
  },
  '/dermatologista': {
    title: 'Dermatologista online: consulta sem sair de casa | Omens',
    description:
      'Consulte um dermatologista online para pele, cabelo e acne, do conforto da sua casa, e receba o tratamento indicado na consulta com discrição.',
    lastmod: '2026-10-26'
  },
  '/emagrecimento': {
    title: 'Emagrecimento com acompanhamento médico online | Omens',
    description:
      'Emagreça com acompanhamento de especialistas online: plano personalizado, tratamento indicado pelo médico quando necessário e suporte contínuo.',
    lastmod: '2026-10-28'
  },
  '/omensmaisfit': {
    title: 'Omens +fit: programa de emagrecimento online | Omens',
    description:
      'O programa de emagrecimento da Omens: acompanhamento de especialistas online, plano personalizado e suporte contínuo.',
    lastmod: '2024-09-13',
    canonical: '/emagrecimento'
  },
  // Treatments and products: no ads, the medication name may appear (PRD 3.3). The medication GEO pages were
  // promoted from their -v2 copies on 28/10/2026 (Morgan).
  '/dapoxetina': {
    title: 'Dapoxetina para ejaculação precoce: como funciona e preço | Omens',
    description:
      'Dapoxetina para ejaculação precoce: como age, para quem é indicada e quais os cuidados. Um urologista online avalia se o tratamento é adequado para você.',
    lastmod: '2026-10-28'
  },
  '/finasterida': {
    title: 'Finasterida para queda de cabelo: oral ou tópica | Omens',
    description:
      'Finasterida oral ou tópica para a calvície: como age, quanto tempo leva para fazer efeito e cuidados. Consulte um dermatologista online antes de começar.',
    lastmod: '2026-10-28'
  },
  '/dutasterida': {
    title: 'Dutasterida para queda de cabelo | Omens',
    description:
      'Dutasterida para a calvície: como age, diferença para a finasterida e cuidados. Consulte um dermatologista online e veja se é indicada para você.',
    lastmod: '2026-10-28'
  },
  '/minoxidil': {
    title: 'Minoxidil para queda de cabelo e barba | Omens',
    description:
      'Minoxidil oral ou tópico para cabelo e barba: como usar, quando começa a fazer efeito e cuidados. Consulte um dermatologista online.',
    lastmod: '2026-10-28'
  },
  '/tetraciclina': {
    title: 'Tetraciclina para acne | Omens',
    description:
      'Tetraciclina para acne: como age, por quanto tempo usar e quais os cuidados. Um dermatologista online avalia se é o tratamento certo para você.',
    lastmod: '2026-10-28'
  },
  '/antibiotico-para-acne': {
    title: 'Antibiótico para acne: quando é indicado | Omens',
    description:
      'Antibióticos orais e tópicos para acne: quando são indicados, por quanto tempo usar e quais os cuidados. Consulte um dermatologista online.',
    lastmod: '2026-10-28'
  },
  '/produtos/avanafil': {
    title: 'Avanafil 50, 100 e 200 mg para disfunção erétil | Omens',
    description:
      'Avanafil para disfunção erétil: dosagens de 50, 100 e 200 mg, como usar e cuidados. Disponível após consulta com urologista online.',
    lastmod: '2023-08-09'
  },
  '/produtos/dapoxetina': {
    title: 'Dapoxetina 30 mg, 30 cápsulas, para ejaculação precoce | Omens',
    description:
      'Dapoxetina 30 mg para ejaculação precoce: ajuda a aumentar a duração da relação e o controle da ejaculação. Disponível após consulta com urologista online.',
    lastmod: '2023-08-09'
  },
  '/produtos/glp1-daily-support': {
    title: 'GLP-1 Daily Support: suplemento diário | Omens',
    description:
      'Suplemento diário para quem faz tratamento para emagrecer com GLP-1, com acompanhamento de especialistas da Omens.',
    lastmod: '2026-04-17'
  },
  '/produtos/mais-fert': {
    title: '+fert: suplemento para a fertilidade masculina | Omens',
    description:
      'Mistura de 14 vitaminas, micronutrientes e antioxidantes naturais desenvolvida por urologistas para a saúde reprodutiva masculina.',
    lastmod: '2024-09-03'
  },
  '/produtos/mais-hair': {
    title: '+hair: suplemento para cabelo e couro cabeludo | Omens',
    description:
      '11 ingredientes, como cobre, selênio, zinco e biotina, formulados por médicos para nutrir o couro cabeludo e apoiar o crescimento do cabelo.',
    lastmod: '2024-09-03'
  },
  '/produtos/mais-lib': {
    title: '+lib: suplemento natural para a libido | Omens',
    description:
      'Suplemento natural desenvolvido pelos médicos da Omens para a libido e o prazer sexual, com ingredientes estudados cientificamente.',
    lastmod: '2024-09-03'
  },
  '/produtos/tribulus-terrestris': {
    title: 'Tribulus terrestris e Androsten em cápsulas | Omens',
    description:
      'Tribulus terrestris 450 ou 1200 mg e Tribulus androsten 94 ou 280 mg: tratamentos naturais para a libido e a função sexual.',
    lastmod: '2023-08-09'
  },
  // Institutional, medical team and authors
  '/como-funciona': {
    title: 'Como funciona a consulta online | Omens',
    description:
      'Preencha o questionário, consulte um especialista por vídeo, telefone ou mensagem e receba em casa o tratamento indicado na consulta. Veja o passo a passo.',
    lastmod: '2023-08-09'
  },
  '/consultar': {
    title: 'Agendar uma consulta online | Omens',
    description: 'Escolha a especialidade e agende a sua consulta online com um médico da Omens.',
    lastmod: '2023-08-09',
    noindex: true
  },
  '/equipe-medica': {
    title: 'Equipe médica | Omens',
    description:
      'Conheça a equipe médica da Omens: médicos brasileiros especialistas em saúde masculina e um comitê científico formado por grandes especialistas.',
    lastmod: '2023-08-09'
  },
  '/author/dr-joao-brunhara': {
    title: 'Dr. João Brunhara, urologista (CRM-SP 161642) | Omens',
    description:
      'Urologista formado pela FMUSP, CRM-SP 161642, RQE 118506. Conheça a formação, as publicações e os conteúdos do Dr. João Brunhara na Omens.',
    lastmod: '2026-10-28'
  },
  '/author/dra-aline-erthal': {
    title: 'Dra. Aline Erthal, dermatologista (CRM-SP 199764) | Omens',
    description:
      'Dermatologista formada pela FMUSP, CRM-SP 199764, RQE 98608. Conheça a formação e os conteúdos da Dra. Aline Erthal na Omens.',
    lastmod: '2026-10-28'
  },
  // Lote 3 (PRD 11): /pesquisas and /imprensa went public on 28/10/2026 (Morgan); the new author page waits.
  '/author/dr-carlos-seraphim': {
    title: 'Dr. Carlos Seraphim, endocrinologista (CRM-SP 156663) | Omens',
    description:
      'Endocrinologista formado pela FMUSP, CRM-SP 156663, RQE 79256. Conheça a formação, as publicações e os conteúdos do Dr. Carlos Seraphim.',
    lastmod: '2026-10-24',
    noindex: true
  },
  '/imprensa': {
    title: 'A Omens na imprensa: matérias que citam a Omens | Omens',
    description:
      'Matérias de jornais, revistas e portais que citam a Omens, plataforma de saúde masculina online: veículo, título, data e link de cada uma.',
    lastmod: '2026-10-28'
  },
  '/pesquisas': {
    title: 'Pesquisas Omens + Datafolha sobre saúde masculina | Omens',
    description:
      'As pesquisas Datafolha encomendadas pela Omens sobre saúde e vida sexual dos brasileiros: período, amostra, margem de erro, números e relatórios.',
    lastmod: '2026-10-28'
  },
  '/quem-somos': {
    title: 'Quem somos | Omens',
    description:
      'A Omens é uma plataforma brasileira de saúde masculina online, criada em 2020 em São Paulo, com urologistas, dermatologistas e psicólogos.',
    lastmod: '2024-02-15'
  },
  '/profissionais': {
    title: 'Especialistas | Omens',
    description:
      'Saúde sexual, pele, cabelo e psicologia: conheça os especialistas que atendem online na Omens, com preços acessíveis.',
    lastmod: '2023-08-09'
  },
  '/faq': {
    title: 'Perguntas frequentes | Omens',
    description:
      'Como funcionam a consulta online, a entrega dos tratamentos, o pagamento e a privacidade na Omens: perguntas e respostas.',
    lastmod: '2023-08-09',
    hostScoped: true
  },
  '/termos-de-uso': {
    title: 'Termos de uso e perguntas frequentes | Omens',
    description: 'Perguntas e respostas sobre a consulta online, a entrega dos tratamentos e o pagamento na Omens.',
    lastmod: '2023-08-09',
    canonical: '/faq',
    hostScoped: true
  },
  '/termos-e-condicoes': {
    title: 'Termos e condições | Omens',
    description: 'Termos e condições de uso da plataforma Omens.',
    lastmod: '2026-10-23',
    hostScoped: true
  },
  '/politica-de-privacidade': {
    title: 'Política de privacidade | Omens',
    description: 'Como a Omens coleta, usa e protege os seus dados pessoais, de acordo com a LGPD.',
    lastmod: '2026-10-23',
    hostScoped: true
  },
  // Datafolha/Omens surveys
  '/problemas-de-erecao-o-grande-tabu': {
    title: 'Pesquisa Datafolha | O grande tabu da disfunção erétil | Omens',
    description:
      'Estudo Datafolha/Omens 2020 sobre os problemas de ereção: 38% dos brasileiros já tiveram algum problema de ereção, e a maioria nunca procurou um especialista.',
    lastmod: '2023-08-09'
  },
  '/problemas-de-erecao-o-grande-tabu-entrevista-joao-brunhara': {
    title: 'Disfunção erétil no Brasil: análise do Dr. João Brunhara | Omens',
    description:
      'O urologista João Brunhara comenta o estudo Datafolha/Omens de 2020 sobre a disfunção erétil no Brasil.',
    lastmod: '2023-08-09'
  },
  '/paquera': {
    title: 'Pesquisa Datafolha | Sexualidade e paquera | Omens + Happn',
    description: 'Estudo Datafolha/Omens + Happn 2021 sobre sexualidade e paquera no Brasil.',
    lastmod: '2023-08-09'
  },
  '/exame-de-toque-retal': {
    title: 'Pesquisa Datafolha | O câncer da próstata e o exame de toque retal | Omens',
    description: 'Pesquisa Datafolha/Omens 2021 sobre o câncer de próstata e o exame de toque retal no Brasil.',
    lastmod: '2023-08-09'
  },
  '/exame-pesquisa-libido-na-pandemia': {
    title: 'Pesquisa Datafolha | Libido na pandemia | Omens',
    description:
      'Estudo Datafolha/Omens 2021: quatro em cada dez brasileiros relataram queda na frequência das relações sexuais na pandemia.',
    lastmod: '2023-08-09'
  },
  '/tamanho-do-penis-no-brasil': {
    title: 'Pesquisa Datafolha | Tamanho do pênis no Brasil | Omens',
    description: 'Pesquisa Datafolha/Omens 2021 sobre o tamanho do pênis no Brasil.',
    lastmod: '2023-08-09'
  },
  '/sexo-virtual': {
    title: 'Pesquisa Datafolha | Sexo virtual no Brasil | Omens',
    description:
      'Perguntamos aos brasileiros o que eles pensam do sexo virtual: veja o que homens e mulheres responderam, sem tabu.',
    lastmod: '2023-08-09'
  },
  '/sexo-e-posicao-politica': {
    title: 'Pesquisa Datafolha | Sexo e posição política | Omens',
    description:
      'As práticas sexuais dos brasileiros e a relação com a orientação política: quem trai mais, quem tem mais orgasmos.',
    lastmod: '2023-08-09'
  },
  '/fetiches-e-praticas-no-sexo': {
    title: 'Pesquisa Datafolha | Fetiches e práticas no sexo | Omens',
    description:
      'As principais práticas sexuais dos brasileiros, os fetiches mais comuns e o grau de satisfação na cama, segundo pesquisa Datafolha/Omens.',
    lastmod: '2023-08-09'
  },
  '/saude-e-bem-estar-no-brasil': {
    title: 'Pesquisa Datafolha | Saúde e bem-estar no Brasil | Omens',
    description:
      'Quanto tempo esperamos por atendimento, quem procura ou evita o médico: o que os brasileiros pensam dos serviços de saúde.',
    lastmod: '2023-08-09'
  },
  // Full reports of the surveys (charts and PDF)
  '/pesquisa-datafolha-problemas-de-erecao': {
    title: 'Relatório completo | O grande tabu da disfunção erétil | Datafolha/Omens',
    description: 'Resultados completos da pesquisa Datafolha/Omens sobre a disfunção erétil no Brasil (2020).',
    lastmod: '2023-08-09'
  },
  '/pesquisa-datafolha-sexualidade-e-paquera': {
    title: 'Relatório completo | Sexualidade e paquera | Datafolha/Omens',
    description: 'Resultados completos da pesquisa Datafolha/Omens Omens + Happn sobre sexualidade e paquera (2021).',
    lastmod: '2023-08-09'
  },
  '/pesquisa-exame-de-toque-retal': {
    title: 'Relatório completo | Câncer de próstata e toque retal | Datafolha/Omens',
    description:
      'Resultados completos da pesquisa Datafolha/Omens sobre o câncer de próstata e o exame de toque retal (2021).',
    lastmod: '2023-08-09'
  },
  '/pesquisa-fetiches-e-praticas-no-sexo': {
    title: 'Relatório completo | Fetiches e práticas no sexo | Datafolha/Omens',
    description:
      'Resultados completos da pesquisa Datafolha/Omens sobre fetiches, práticas sexuais e satisfação dos brasileiros.',
    lastmod: '2023-08-09'
  },
  '/pesquisa-libido-na-pandemia': {
    title: 'Relatório completo | Libido na pandemia | Datafolha/Omens',
    description: 'Resultados completos da pesquisa Datafolha/Omens sobre a libido dos brasileiros na pandemia (2021).',
    lastmod: '2023-08-09'
  },
  '/pesquisa-saude-e-bem-estar-no-brasil': {
    title: 'Relatório completo | Saúde e bem-estar no Brasil | Datafolha/Omens',
    description:
      'Resultados completos da pesquisa Datafolha/Omens sobre o que os brasileiros pensam dos serviços de saúde.',
    lastmod: '2023-08-09'
  },
  '/pesquisa-sexo-e-posicao-politica': {
    title: 'Relatório completo | Sexo e posição política | Datafolha/Omens',
    description:
      'Resultados completos da pesquisa Datafolha/Omens sobre as práticas sexuais dos brasileiros e a orientação política.',
    lastmod: '2023-08-09'
  },
  '/pesquisa-sexo-virtual': {
    title: 'Relatório completo | Sexo virtual no Brasil | Datafolha/Omens',
    description: 'Resultados completos da pesquisa Datafolha/Omens sobre o que os brasileiros pensam do sexo virtual.',
    lastmod: '2023-08-09'
  },
  '/pesquisa-tamanho-do-penis-no-brasil': {
    title: 'Relatório completo | Tamanho do pênis no Brasil | Datafolha/Omens',
    description: 'Resultados completos da pesquisa Datafolha/Omens sobre o tamanho do pênis no Brasil (2021).',
    lastmod: '2023-08-09'
  }
}

/**
 * Pages that can be Google Ads destinations (PRD section 4): the home, the pillars (3.5), the duplicates (3.1)
 * and the long-tail pages (3.2). Nothing in them (text, FAQ, metadata, JSON-LD) may say "receita"/"prescrição"
 * or name a medication; the medication pages (3.3), /produtos and the Control SEO landings may.
 */
export const OMENS_ADS_PAGES = [
  '/',
  '/ejaculacao-precoce',
  '/disfuncao-eretil',
  '/perda-de-libido',
  '/prostata-aumentada',
  '/prep',
  '/harmonizacao-peniana',
  '/calvicie',
  '/acne',
  '/dermatite',
  '/emagrecimento',
  '/psicologia-sexual-parceria',
  '/saude-sexual',
  '/dermatologia',
  '/hpb',
  '/omensmaisfit',
  '/termos-de-uso',
  '/impotencia-sexual',
  '/quando-o-homem-ejacula-rapido',
  '/problemas-sexuais',
  '/dermatologista'
] as const

/** What an Ads page must never contain (PRD section 4): prescription words and medication/active ingredient names. */
export const ADS_FORBIDDEN_TERMS =
  /\b(?:receit\w*|prescri\w*|prescrev\w*|dapoxetina|paroxetina|sertralina|fluoxetina|clomipramina|tadalafila?|sildenafila?|vardenafila?|avanafila?|viagra|cialis|finasterida|dutasterida|minoxidil|tetraciclina|doxiciclina|minociclina|isotretino\w+|roacutan|adapaleno|tretino\w+|per[óo]xido de benzo\w+|cetoconazol\w*|lidoca\w+|priloca\w+|benzoca\w+|semaglutida|tirzepatida|liraglutida|ozempic|wegovy|mounjaro|saxenda|tenofovir|entricitabina|truvada|tansulosina|espironolactona)\b/i
